import React, { useState, useEffect, useRef } from "react";
import {
  Lock,
  Calendar,
  Clock,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  X,
  CreditCard,
} from "lucide-react";
import {
  createSubscriptionOrder,
  verifySubscriptionPayment,
} from "../../api/partner/partnerApi";
import { showToast } from "../common/notification/Notification";
import "./PartnerGuardModal.css";

export const PartnerExpiredPlanModal = ({
  visible,
  subscription,
  token,
  onSuccess,
}) => {
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);

  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  if (!visible) return null;

  const plan = subscription?.plan || subscription?.subscriptionPlan || null;
  const isPending = subscription?.status === "PENDING";
  const planPrice = plan?.base_price ?? plan?.price ?? plan?.total_price ?? "0";
  const planCycle = plan?.billing_cycle || "YEARLY";

  // Format Date
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  // Days left
  const getDaysLeft = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate).getTime();
    if (isNaN(end)) return 0;
    const now = Date.now();
    const diff = end - now;
    return Math.max(Math.ceil(diff / (1000 * 60 * 60 * 24)), 0);
  };

  // Silently check payment verification
  const checkPaymentSilently = async (orderId) => {
    try {
      const res = await verifySubscriptionPayment(token, orderId, "prod");
      const isSuccess =
        res?.statusType === "SUCCESS" ||
        res?.payment_status === "SUCCESS" ||
        res?.status === "SUCCESS";

      if (isSuccess) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setShowPaymentModal(false);
        showToast("Payment Successful! Your subscription is now activated.", {
          type: "success",
        });
        if (onSuccess) onSuccess();
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  // Initiate Payment / Order creation
  const handleContinuePayment = async () => {
    const subId = subscription?.id || subscription?.subscription_plan_id || plan?.id;
    if (!subId) {
      showToast("Subscription ID not found. Please refresh the page.", {
        type: "error",
      });
      return;
    }

    setPaymentLoading(true);
    try {
      const orderData = await createSubscriptionOrder(token, subId, "prod");

      if (
        orderData?.statusType !== "SUCCESS" &&
        orderData?.status !== "SUCCESS"
      ) {
        showToast(orderData?.text || orderData?.message || "Unable to create payment order.", {
          type: "error",
        });
        return;
      }

      const orderInfo = orderData?.cashfreeOrderResponse || orderData?.data;
      const orderToken = orderInfo?.order_token || orderData?.order_token;
      const orderId = orderInfo?.order_id || orderData?.order_id;
      const paymentSessionUrl =
        orderData?.payment_link ||
        orderInfo?.payment_link ||
        (orderToken ? `https://payments.cashfree.com/forms/${orderToken}` : null);

      if (!paymentSessionUrl && !orderToken) {
        showToast("Invalid payment gateway response.", { type: "error" });
        return;
      }

      setCurrentOrderId(orderId);
      setPaymentUrl(paymentSessionUrl);
      setShowPaymentModal(true);

      // Start automatic polling every 8 seconds
      if (orderId) {
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = setInterval(() => {
          checkPaymentSilently(orderId);
        }, 8000);
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      showToast("Something went wrong while initiating payment. Please try again.", {
        type: "error",
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  // Manual Verification Trigger
  const handleManualVerify = async () => {
    if (!currentOrderId) {
      showToast("No active payment session found to verify.", { type: "error" });
      return;
    }
    setVerifyLoading(true);
    try {
      const verified = await checkPaymentSilently(currentOrderId);
      if (!verified) {
        showToast("Payment is still processing or not found. Please wait or retry in a few moments.", {
          type: "warning",
        });
      }
    } catch (err) {
      showToast("Failed to verify payment status.", { type: "error" });
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <>
      <div className="partner-guard-overlay-backdrop">
        <div className="partner-guard-modal">
          {/* Icon */}
          <div
            className={`partner-guard-icon-circle ${
              isPending
                ? "partner-guard-icon-circle--amber"
                : "partner-guard-icon-circle--danger"
            }`}
          >
            <Lock size={32} />
          </div>

          {/* Title */}
          <h2 className="partner-guard-title">
            {isPending ? "Subscription Payment Pending" : "Partner Subscription Expired"}
          </h2>

          {/* Subtitle */}
          <p className="partner-guard-subtitle">
            {isPending
              ? "Your subscription payment is pending. Please complete your payment to restore full partner dashboard and lead management access."
              : "Your subscription plan is currently expired or inactive. Renew your plan to continue receiving verified client leads and listings."}
          </p>

          {/* Plan Card */}
          <div className="partner-guard-modal-body">
            {plan && (
              <div className="partner-guard-plan-card">
                <div className="partner-guard-plan-header">
                  <span className="partner-guard-plan-name">
                    {plan.plan_name || plan.name || "Partner Plan"}
                  </span>
                  <span
                    className={`partner-guard-badge ${
                      isPending
                        ? "partner-guard-badge--pending"
                        : "partner-guard-badge--expired"
                    }`}
                  >
                    {isPending ? "Payment Pending" : "Expired"}
                  </span>
                </div>

                <div className="partner-guard-price">
                  ₹{Number(planPrice).toLocaleString("en-IN")}{" "}
                  <span className="partner-guard-price-cycle">/ {planCycle}</span>
                </div>

                {subscription?.end_date && (
                  <>
                    <div className="partner-guard-info-row">
                      <Calendar size={15} />
                      <span>Ended on: {formatDate(subscription.end_date)}</span>
                    </div>
                    <div className="partner-guard-info-row">
                      <Clock size={15} />
                      <span>{getDaysLeft(subscription.end_date)} Days Remaining</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className="partner-guard-modal-footer">
            <button
              type="button"
              className="partner-guard-btn-primary w-100"
              onClick={handleContinuePayment}
              disabled={paymentLoading}
            >
              {paymentLoading ? (
                <>
                  <RefreshCw size={16} className="partner-spin" />
                  <span>Connecting to Gateway...</span>
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  <span>{isPending ? "Complete Payment Now" : "Renew / Repurchase Plan"}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Payment Gateway Modal / Iframe Overlay */}
      {showPaymentModal && paymentUrl && (
        <div className="partner-payment-webview-modal">
          <div className="partner-payment-webview-card">
            <div className="partner-payment-webview-header">
              <span className="fw-bold text-dark d-flex align-items-center gap-2">
                <CreditCard size={18} className="text-primary" />
                <span>Complete Subscription Payment</span>
              </span>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                  onClick={handleManualVerify}
                  disabled={verifyLoading}
                >
                  <RefreshCw size={13} className={verifyLoading ? "partner-spin" : ""} />
                  <span>{verifyLoading ? "Verifying..." : "I've Paid"}</span>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1"
                  onClick={() => {
                    if (pollingIntervalRef.current) {
                      clearInterval(pollingIntervalRef.current);
                      pollingIntervalRef.current = null;
                    }
                    setShowPaymentModal(false);
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <iframe
              src={paymentUrl}
              title="Cashfree Payment Gateway"
              className="partner-payment-iframe"
              allow="payment"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PartnerExpiredPlanModal;
